<template>
  <div id="app">
    <b-navbar type="light" variant="light" toggleable="lg">
      <b-navbar-brand href="/">
        <img class="navbar-icon" :src="brandIcon" alt="" />
        {{ brandName }}
      </b-navbar-brand>
      <b-nav-toggle target="nav-collapse" />

      <b-collapse id="nav-collapse" is-nav>
        <a
          v-if="dashboardContactEmail"
          :href="'mailto:' + dashboardContactEmail"
          style="margin-right: 10px"
        >
          <span v-label-key="'CONTACT_US'" style="white-space: nowrap;"
            >Contact Us</span
          >
        </a>

        <b-nav-form v-if="auth && auth.user" class="my-2 my-lg-0">
          <autocomplete
            class="autocomplete-input"
            :items="$store.state.global.autocomplete"
            :title="
              $store.getters['translations/getLabel']('SEARCH_PLACEHOLDER')
            "
          />
        </b-nav-form>
        <b-navbar-nav class="ml-auto">
          <b-nav-item-dropdown v-show="languages && languages.length" right>
            <template v-slot:button-content>
              <i style="font-size:20px" class="fa">&#xf1ab;</i>
            </template>
            <b-dropdown-item
              v-for="language in languages"
              :key="language.code"
              @click="selectLanguage(language)"
            >
              {{ language.local_name }}
              <i
                v-if="selectedLanguage === language.code"
                style="font-size:15px"
                class="fa"
                >&#xf00c;</i
              >
            </b-dropdown-item>
          </b-nav-item-dropdown>
          <template v-if="auth && auth.user">
            <b-nav-item v-router-link="{ name: 'home' }">
              <span style="white-space: nowrap;">
                <i style="font-size:15px" class="fa">&#xf015;</i>&nbsp;
                <span v-label-key="'HOME'">Home</span>
              </span>
            </b-nav-item>
            <b-nav-item-dropdown right>
              <template v-slot:button-content>
                <span v-label-key="'MY_NETWORK'">My Network</span>
              </template>
              <b-dropdown-item
                v-if="auth.user.selectedAccess.roleLevel > 2"
                v-router-link="{ name: 'distributor-list' }"
              >
                <span v-label-key="'DISTRIBUTORS'">Distributors</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="auth.user.selectedAccess.roleLevel > 1"
                v-router-link="{ name: 'subdistributor-list' }"
              >
                <span v-label-key="'SUBDISTRIBUTORS'">Subdistributors</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="auth.user.selectedAccess.roleLevel > 0"
                v-router-link="{ name: 'chain-list' }"
              >
                <span v-label-key="'CHAINS'">Chains</span></b-dropdown-item
              >
              <b-dropdown-item v-router-link="{ name: 'store-list' }">
                <span v-label-key="'STORES'">Stores</span></b-dropdown-item
              >
              <b-dropdown-item
                v-if="auth.user.selectedAccess.roleLevel > 3"
                v-router-link="{ name: 'user-list' }"
                ><span v-label-key="'USERS'"> Users</span>
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item-dropdown right>
              <template v-slot:button-content>
                <span v-label-key="'TOOLS'"> Tools</span>
              </template>
              <b-dropdown-item :href="tutorialLink" target="_blank">
                <span v-label-key="'TUTORIAL'">Tutorial</span>
              </b-dropdown-item>
              <b-dropdown-item v-router-link="{ name: 'filmcodeinspector' }"
                ><span v-label-key="'FILM_CODE_INSPECTOR'">
                  Film Code Inspector</span
                >
              </b-dropdown-item>
              <b-dropdown-item v-router-link="{ name: 'voucherinspector' }"
                ><span v-label-key="'FOUR_SEASONS_VOUCHER_INSPECTOR'">
                  Four Seasons Voucher Inspector</span
                >
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <b-nav-item-dropdown right>
              <template v-slot:button-content>
                {{ auth.user.first_name }} ({{ auth.user.selectedAccess.name }})
                <i style="font-size:15px" class="fa">&#xf007;</i>
              </template>
              <b-dropdown-item
                v-for="access in userAccess"
                :key="access.id"
                @click="switchAccess(access)"
              >
                <span v-label-key="'SWITCH_TO_ACCESS_NAME'">Switch to</span
                >&nbsp;{{ access.name }}
              </b-dropdown-item>
              <b-dropdown-item @click="logout()">
                <span v-label-key="'LOGOUT'">Logout</span>&nbsp;
                <i class="fa fa-power-off" />
              </b-dropdown-item>
            </b-nav-item-dropdown>
          </template>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>
    <router-view />
    <div style="text-align:center;color:#aaa;font-size:11px;">
      {{ appVersion }}
    </div>
  </div>
</template>

<script>
import $api from '@/services/api';
import $auth from '@/services/auth';
import { mapState } from 'vuex';
import Autocomplete from './components/Autocomplete';

const addFavicon = () => {
  const fv = document.createElement('link');
  fv.setAttribute('rel', 'shortcut icon');
  fv.setAttribute('type', 'image/png');
  fv.setAttribute('href', '/icon.png');
  document.head.appendChild(fv);
};

const addCustomStyle = (url) => {
  const styleEl = document.createElement('link');
  styleEl.setAttribute('rel', 'stylesheet');
  styleEl.setAttribute('href', `https://cdn.gokaizen.com/${url}`);
  document.head.appendChild(styleEl);
};

export default {
  components: {
    Autocomplete,
  },

  data() {
    return {
      auth: $auth,
      appVersion: process.env.VUE_APP_RELEASE,
      domainInfo: null,
    };
  },

  computed: {
    userAccess() {
      return this.auth.user.access.filter(
        (access) => access !== this.auth.user.selectedAccess,
      );
    },
    brandName() {
      return (this.domainInfo && this.domainInfo.brandName) || '';
    },
    brandIcon() {
      return (this.domainInfo && this.domainInfo.brandIcon) || '';
    },
    ...mapState('languages', {
      languages: 'languages',
      selectedLanguage: 'language',
    }),
    dashboardContactEmail() {
      return (this.domainInfo && this.domainInfo.brandContact) || '';
    },
    tutorialLink() {
      return (this.domainInfo && this.domainInfo.tutorialLink) || '';
    },
  },

  async mounted() {
    this.$store.dispatch('languages/fetch');

    const domain = window.location.hostname;
    // const domain = 'www.dashboardtest.com';
    if (domain === 'dashboard.gokaizen.com') {
      this.domainInfo = {
        brandName: 'KaizenOS',
        brandIcon: 'icon.png',
        tutorialLink: 'https://www.youtube.com/@GoKaizen',
      };
      addFavicon();
    } else {
      const domainInfo = await $api.get(
        `/v1/dashboard/domain?domain=${domain}`,
      );
      if (!domainInfo) {
        this.domainInfo = {
          brandName: 'KaizenOS',
          brandIcon: 'icon.png',
          tutorialLink: 'https://www.youtube.com/@GoKaizen',
        };
      } else {
        this.domainInfo = {
          brandName: domainInfo.name,
          brandIcon: `https://cdn.gokaizen.com/${domainInfo.logo}`,
          brandContact: domainInfo.dashboard_contact_email,
          tutorialLink:
            domainInfo.tutorial_link || 'https://www.youtube.com/@GoKaizen',
        };
        if (domainInfo.custom_css) {
          addCustomStyle(domainInfo.custom_css);
        }
      }
    }
  },

  methods: {
    logout() {
      localStorage.clear();
      window.location.href = '/';
    },
    switchAccess(access) {
      localStorage.accessId = access.id;
      $auth.user.selectedAccess = access;
      window.location.href = '/';
    },
    selectLanguage(language) {
      this.$store.dispatch('languages/setLanguage', language.code);
    },
  },
};
</script>
<style>
.navbar-icon {
  height: 28px;
  border-radius: 5px;
}

.router-link {
  cursor: pointer;
}

.router-link:hover {
  text-decoration: none;
}

.nav-item {
  cursor: pointer;
}

a {
  color: inherit;
}

th[role='columnheader'] {
  outline: none;
}

a.nav-link.router-link-exact-active {
  color: #495057;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  border-bottom-color: transparent;
}

.nav-tabs .nav-link.router-link-exact-active:focus,
.nav-tabs .nav-link.router-link-exact-active:hover {
  border-bottom-color: transparent;
}
.autocomplete.autocomplete-input {
  width: unset;
}
.autocomplete.autocomplete-input .options {
  min-width: 300px;
}
</style>
